import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { TableNames } from "../../../enums/tableNames";
import { RootState } from "../../../redux/store/store";
import { makePostCallForInitialDataFetch } from "../apis/makePostCallForInitialDataFetch";
import { buildWorklistFetchPayload } from "../utils/buildWorklistFetchPayload";
import { CallingWorklistView } from "../utils/buildWorklistFilters";
import { CallingWorklistTableState } from "./useCallingWorklistTableData";

export interface UseInitialDataFetchingForTableReturn {
  initialDataFetchingExecuted: boolean;
  setInitialDataFetchingExecuted: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Loads the first page of the calling worklist when the table mounts or resets.
 *
 * @input setPharmaStpFileState - State setter for the API response wrapper
 * @input setTableData - State setter for the table rows
 * @input pageSize - Rows per page
 * @input isArchiveView - When true, initial load uses the archive list payload
 * @input view - Worklist the payload is for
 * @input tableName - Table whose search text and refetch flags this list owns
 * @return Whether the initial fetch has completed, plus a setter to re-trigger it
 */
export const useInitialDataFetchingForTable = (
  setPharmaStpFileState: React.Dispatch<
    React.SetStateAction<CallingWorklistTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<PharmaStpFileModel[]>>,
  pageSize: number,
  /** When true, initial load and refresh use the archive list payload (same as archive icon). */
  isArchiveView: boolean,
  /** Worklist the payload is for; the Pending list also filters on response status. */
  view: CallingWorklistView = CallingWorklistView.NON_EPA,
  /** Table whose search text and refetch flags this list owns. */
  tableName: TableNames = TableNames.CALLING_WORKLIST_ORDERS,
): UseInitialDataFetchingForTableReturn => {
  const { setTableDataForContext, getTableDataFromContext } =
    useTablesContext();
  const [initialDataFetchingExecuted, setInitialDataFetchingExecuted] =
    useState<boolean>(false);

  const tableAttributes = getTableDataFromContext(tableName);

  const { filters } = useSelector((state: RootState) => state.filterValues);

  const validFilters = useMemo(() => {
    return filters.filter((filter) => filter.name === tableName);
  }, [filters, tableName]);

  const setPharmaStpFileStateRef = useRef(setPharmaStpFileState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  useEffect(() => {
    setPharmaStpFileStateRef.current = setPharmaStpFileState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [setPharmaStpFileState, setTableData, setTableDataForContext]);

  useEffect(() => {
    if (!initialDataFetchingExecuted) {
      const payload = buildWorklistFetchPayload({
        view,
        isArchiveView,
        validFilters,
        searchTerm: tableAttributes?.searchedText ?? "",
        pageSize,
      });

      makePostCallForInitialDataFetch(
        payload,
        setPharmaStpFileStateRef.current,
        setTableDataRef.current,
      ).then(() => {
        setInitialDataFetchingExecuted(true);

        setTableDataForContextRef.current(tableName, {
          lastBatchFetched: 1,
        });
      });
    }
  }, [
    initialDataFetchingExecuted,
    pageSize,
    tableAttributes?.searchedText,
    validFilters,
    isArchiveView,
    view,
    tableName,
  ]);

  return {
    initialDataFetchingExecuted,
    setInitialDataFetchingExecuted,
  };
};
